import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import useNotificationStore from '../../store/notificationStore';

// --- 스타일 ---
const HistoryContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9995; /* 마스코트보다 위 */
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const BellButton = styled.button`
  width: 48px; height: 48px;
  border-radius: 50%;
  background-color: white;
  border: 1px solid var(--border-color);
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  position: relative;
  transition: transform 0.2s;
  
  &:hover { transform: scale(1.1); background-color: var(--bg-input); }
  
  svg { width: 24px; height: 24px; fill: var(--text-dark); }
`;

const Badge = styled.span`
  position: absolute; top: -5px; right: -5px;
  background-color: var(--danger-accent); color: white;
  font-size: 0.7rem; font-weight: bold;
  padding: 2px 6px; border-radius: 10px;
  border: 2px solid white;
`;

const HistoryPopup = styled.div<{ isOpen: boolean }>`
  position: absolute;
  bottom: 60px; right: 0;
  width: 320px;
  max-height: 400px;
  background-color: white;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.15);
  display: flex; flex-direction: column;
  overflow: hidden;
  transform-origin: bottom right;
  transition: all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1);
  
  /* 토글 애니메이션 */
  opacity: ${props => props.isOpen ? 1 : 0};
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.8)'};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
`;

const PopupHeader = styled.div`
  padding: 12px 16px;
  background-color: var(--bg-list);
  border-bottom: 1px solid var(--border-color);
  font-weight: bold; color: var(--text-bright);
  display: flex; justify-content: space-between; align-items: center;
`;

const ClearButton = styled.button`
  font-size: 0.75rem; color: var(--text-normal); background: none; border: none; cursor: pointer;
  &:hover { text-decoration: underline; color: var(--danger-accent); }
`;

const ListContainer = styled.div`
  flex: 1; overflow-y: auto;
  max-height: 350px;
  
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: #ddd; border-radius: 3px; }
`;

const NotificationItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  &:last-child { border-bottom: none; }
  &:hover { background-color: #fafafa; }
`;

const ItemHeader = styled.div`
  display: flex; justify-content: space-between; margin-bottom: 4px;
`;
const ItemTitle = styled.span`font-size: 0.9rem; font-weight: 600; color: #333;`;
const ItemTime = styled.span`font-size: 0.75rem; color: #999;`;
const ItemContent = styled.div` // p 태그에서 div로 변경 권장
  font-size: 0.85rem;
  color: #555;
  margin: 0;
  line-height: 1.4;

  /* ✨ 에디터 결과물 스타일 대응 */
  img { max-width: 100%; height: auto; border-radius: 4px; display: block; margin: 5px 0; }
  p { margin: 0 0 5px 0; }
  strong { font-weight: bold; }
`;

const EmptyState = styled.div`
  padding: 2rem; text-align: center; color: #999; font-size: 0.9rem;
`;

const formatTime = (ts: number) => {
    const date = new Date(ts);
    return `${date.getHours().toString().padStart(2,'0')}:${date.getMinutes().toString().padStart(2,'0')}`;
};

const NotificationHistory = () => {
    // ✨ getUnreadCount 가져오기
    const { history, isHistoryOpen, toggleHistory, clearHistory, getUnreadCount } = useNotificationStore();
    const popupRef = useRef<HTMLDivElement>(null);

    // ✨ 렌더링 시마다 개수 계산
    const unreadCount = getUnreadCount();
    // 바깥 클릭 시 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                if (isHistoryOpen) toggleHistory();
            }
        };
        // 캡처링 단계가 아닌 버블링 단계에서 처리, 버튼 클릭과 충돌 방지 위해 약간의 꼼수
        if (isHistoryOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isHistoryOpen, toggleHistory]);

    return (
        <HistoryContainer ref={popupRef}>
            <HistoryPopup isOpen={isHistoryOpen}>
                <PopupHeader>
                    <span>알림 보관함</span>
                    {history.length > 0 && (
                        <ClearButton onClick={clearHistory}>모두 지우기</ClearButton>
                    )}
                </PopupHeader>
                <ListContainer>
                    {history.length === 0 ? (
                        <EmptyState>받은 알림이 없습니다.</EmptyState>
                    ) : (
                        history.map((msg) => (
                            <NotificationItem key={msg.id} style={{ opacity: msg.read ? 0.6 : 1 }}>
                                {/* 읽은 건 흐리게 표시하는 예시 */}
                                <ItemHeader>
                                    <ItemTitle>
                                        {msg.title}
                                        {!msg.read && <span style={{color:'red', fontSize:'0.6rem', marginLeft:'4px'}}>●</span>}
                                    </ItemTitle>
                                    <ItemTime>{formatTime(msg.timestamp)}</ItemTime>
                                </ItemHeader>
                                <ItemContent
                                    dangerouslySetInnerHTML={{ __html: msg.content }}
                                />
                            </NotificationItem>
                        ))
                    )}
                </ListContainer>
            </HistoryPopup>

            <BellButton onClick={toggleHistory}>
                <svg viewBox="0 0 24 24">
                    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z" />
                </svg>
                {/* ✨ 안 읽은 게 있을 때만 배지 표시 */}
                {unreadCount > 0 && <Badge>{unreadCount}</Badge>}
            </BellButton>
        </HistoryContainer>
    );
};

export default NotificationHistory;